<template>
  <div>
    <div class="my-4 d-flex" style="gap: 1rem;">
      <v-btn variant="flat" color="primary" @click="() => isImportDialogOpen = true">発達記録をインポート</v-btn>
      <v-btn variant="outlined" color="primary" @click="exportFile('domainField')">発達項目をエクスポート</v-btn>
      <v-btn variant="outlined" color="primary" @click="exportFile('fieldClass')">クラス紐付けをエクスポート</v-btn>
      <v-spacer />
      <!-- <v-btn
        variant="outlined"
        color="primary"
        @click="updateDevelopmentRecords"
        :loading="isFetching"
      >分析を更新</v-btn> -->
    </div>
    <div style="width: 160px;" class="ma-2">
      <v-text-field hide-details density="compact" variant="outlined" type="number" v-model="year"
        suffix="年度"></v-text-field>
    </div>
    <v-progress-linear v-if="isFetching" indeterminate color="primary"></v-progress-linear>
    <v-table>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              クラス名
            </th>
            <th class="text-left">
              年齢
            </th>
            <th></th>
            <th class="text-left">Excel出力</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in classes" :key="c.nurseryClass.classId">
            <td>{{ c.nurseryClass.className }}</td>
            <td>{{ c.nurseryClass.childrenAge }}</td>
            <!-- <td>{{ formatDate(c.lastUpdatedAt) }}</td> -->
            <td>
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <v-btn color="primary" variant="outlined"
                  :to="`/child-development/class/${year}/${c.nurseryClass.classId}`">
                  編集
                </v-btn>
              </div>
            </td>
            <td>
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <v-btn small color="primary" rounded depressed
                  :loading="exportingClassId === c.nurseryClass.classId && exportingType === 'development'"
                  @click="exportChildDevelopment(c.nurseryClass.classId)">
                  <v-icon small left>mdi-file-excel-outline</v-icon>
                  発達記録表出力
                </v-btn>
                <v-btn small color="primary" rounded depressed
                  :loading="exportingClassId === c.nurseryClass.classId && exportingType === 'record'"
                  @click="exportChildDevelopmentRecord(c.nurseryClass.classId)">
                  <v-icon small left>mdi-file-excel-outline</v-icon>
                  経過記録出力
                </v-btn>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>

    <!-- インポートダイアログ -->
    <v-dialog max-width="700" v-model="isImportDialogOpen" @click:outside="closeImportDialog">
      <v-card class="pa-4">
        <page-title :value="`発達記録をインポート`" />

        <div class="my-4">
          <v-file-input truncate-length="15" accept=".csv" placeholder="CSVファイルを選択してください"
            v-model="importFile"></v-file-input>
        </div>

        <v-radio-group v-model="fileType" inline class="my-4">
          <v-radio label="発達項目CSV" value="domainField"></v-radio>
          <v-radio label="クラス紐付けCSV" value="fieldClass"></v-radio>
        </v-radio-group>

        <v-progress-linear v-if="isImportProcessing" indeterminate color="primary" class="my-4" />

        <div class="my-4 d-flex align-center">
          <v-spacer />
          <v-btn color="primary" variant="flat" :disabled="isImportProcessing" @click="startImport">インポートを開始</v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import CommonUtil from '@/utils/CommonUtil';
import dayjs from 'dayjs';
import { defineComponent } from 'vue';
import PageTitle from '@/components/atoms/PageTitle.vue';

import { childDevelopmentsModule } from '@/store/viewModules/child-development/childDevelopmentsModule'
import LocalDataService from '@/service/LocalDataService';
import ApiChildDevelopment from '@/api/ApiChildDevelopment';
import ApiResource from '@/api/ApiResource';

interface DataType {
  isFetching: boolean;
  // showQuitChildren: boolean;
  // showDonwloadMessage: boolean;
  year: number;
  isImportDialogOpen: boolean;
  isImportProcessing: boolean;
  importFile?: File;
  fileType: string;
  watchingTimer: number | null;
  exportingClassId: number | null;
  exportingType: string | null;
}

export default defineComponent({
  components: {
    PageTitle
  },
  data: (): DataType => {
    return {
      isFetching: false,
      year: dayjs().year(),
      isImportDialogOpen: false,
      isImportProcessing: false,
      importFile: undefined,
      fileType: 'domainField',
      watchingTimer: null,
      exportingClassId: null,
      exportingType: null
    }
  },
  computed: {
    classes() {
      return childDevelopmentsModule.classes
    },
    importStatuses() {
      return childDevelopmentsModule.statuses
    }
  },
  methods: {
    async listDevelopmentRecords() {
      this.isFetching = true;
      await childDevelopmentsModule.listClasses({ year: this.year });
      this.isFetching = false;
    },
    async updateDevelopmentRecords() {
      this.isFetching = true;
      await childDevelopmentsModule.updateRecords({ year: this.year });
      await this.listDevelopmentRecords();
      this.isFetching = false;
    },
    async openFile(path: string | null) {
      const nurseryId = LocalDataService.getNurseryId();
      if (path && nurseryId) {
        this.isFetching = true;
        const url = await ApiResource.getDownloadUrl({
          nurseryId,
          path
        })
        this.isFetching = false;
        window.open(url.preSignedDownloadUrl, '_blank')
      }
    },
    formatDate(dateString: string | null) {
      if (!dateString) {
        return '不明';
      }
      return dayjs(dateString).format('YYYY-MM-DD HH:mm')
    },
    closeImportDialog() {
      this.isImportDialogOpen = false;
    },
    async startImport() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.importFile) {
        this.isImportProcessing = true;
        const uploadTargetResponse = await ApiChildDevelopment.getImportUploadUrl({
          nurseryId,
          fileType: this.fileType
        });
        if (uploadTargetResponse) {
          const uploadResponse = await ApiResource.uploadFile({
            url: uploadTargetResponse.preSignedUploadUrl,
            file: this.importFile,
          });
          this.startWatchingStatus({
            statusId: String(uploadTargetResponse.statusId),
            category: uploadTargetResponse.category
          })
          alert('インポートを開始しました')
        }
        this.isImportProcessing = false;
        this.closeImportDialog();
      }
    },
    startWatchingStatus({
      category,
      statusId,
    }: {
      statusId: string;
      category: string;
    }) {
      this.watchingTimer = setInterval(async () => {
        const response = await childDevelopmentsModule.listImportStatus({
          statusId,
          category
        })

        if (response && response.status === 'success') {
          alert('インポートが完了しました')
          this.stopWatchingStatus();
        }

        if (response && response.status === 'failed') {
          alert('インポートに失敗しました。ファイルの内容を確認してください。')
          this.stopWatchingStatus();
        }
      }, 2500);
    },
    stopWatchingStatus() {
      if (this.watchingTimer) {
        clearInterval(this.watchingTimer)
        this.watchingTimer = null;
      }
    },
    async exportChildDevelopment(classId: number) {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) return;
      this.exportingClassId = classId;
      this.exportingType = 'development';
      try {
        const response = await ApiChildDevelopment.exportChildDevelopment({
          nurseryId,
          classId,
          year: this.year,
        });
        if (response) {
          alert('エクスポートを開始しました。\nダウンロード一覧画面からご確認ください。\n※完了まで1-2分程度かかる場合があります。');
        } else {
          alert('エクスポートに失敗しました');
        }
      } catch {
        alert('エクスポートに失敗しました');
      } finally {
        this.exportingClassId = null;
        this.exportingType = null;
      }
    },
    async exportChildDevelopmentRecord(classId: number) {
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) return;
      this.exportingClassId = classId;
      this.exportingType = 'record';
      try {
        const response = await ApiChildDevelopment.exportChildDevelopmentRecord({
          nurseryId,
          classId,
          year: this.year,
        });
        if (response) {
          alert('エクスポートを開始しました。\nダウンロード一覧画面からご確認ください。\n※完了まで1-2分程度かかる場合があります。');
        } else {
          alert('エクスポートに失敗しました');
        }
      } catch {
        alert('エクスポートに失敗しました');
      } finally {
        this.exportingClassId = null;
        this.exportingType = null;
      }
    },
    async exportFile(fileType: string) {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiChildDevelopment.getExportUrl({
          nurseryId,
          fileType
        })

        if (response) {
          window.open(response.preSignedDownloadUrl, '_blank');
        }
      }
    }
  },
  watch: {
    year() {
      this.listDevelopmentRecords();
    }
  },
  mounted() {
    this.year = parseInt(CommonUtil.getFiscalYear(dayjs().format('YYYY-MM')))
    this.listDevelopmentRecords();
  }
})
</script>